/**
 * Sezione "Contatti" nel dettaglio cliente (Slice A — Forniture).
 *
 * Mostra la lista dei contatti del cliente (email, ruolo, data aggiunta) e,
 * solo per chi ha PORTFOLIO_MANAGEMENT, un form per aggiungerne uno nuovo e un
 * pulsante "Rimuovi" per ogni riga. Gli utenti con sola VIEW_PORTFOLIO vedono
 * la lista in sola lettura (pattern coerente con OwnershipSection).
 *
 * I ruoli disponibili sono 'responsabile', 'distribuzione' e 'security'
 * (estensione di OwnershipRole con il ruolo 'security' per i referenti di sicurezza).
 */

import { useState, type FormEvent } from 'react';
import { usePermissions } from '../../auth/index.js';
import { useContacts, useAddContact, useRemoveContact } from './hooks.js';
import type { ContactRole } from './types.js';
import styles from './Customers.module.css';

/** Etichette leggibili per i ruoli contatto. */
const ETICHETTE_RUOLO: Record<ContactRole, string> = {
  responsabile: 'Responsabile',
  distribuzione: 'Distribuzione',
  security: 'Security',
  segnalante: 'Segnalante',
};

/** Form per aggiungere un contatto al cliente. */
function NuovoContattoForm({ customerId }: { customerId: number }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ContactRole>('responsabile');
  const { mutate, isPending, isError, error } = useAddContact(customerId);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutate(
      { email: email.trim(), role },
      {
        onSuccess: () => {
          setEmail('');
          setRole('responsabile');
        },
      },
    );
  }

  function messaggioErrore(): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return "Errore durante l'aggiunta del contatto.";
  }

  return (
    <div className={styles.formCard} data-testid="form-aggiungi-contatto">
      <p className={styles.formTitle}>Aggiungi contatto</p>
      <form onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={styles.formGroup} style={{ flex: 3 }}>
            <label className={styles.formLabel} htmlFor="contatto-email">
              Email *
            </label>
            <input
              id="contatto-email"
              type="email"
              className={styles.formInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="es. referente@example.com"
              data-testid="input-contatto-email"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="contatto-role">
              Ruolo *
            </label>
            <select
              id="contatto-role"
              className={styles.formSelect}
              value={role}
              onChange={(e) => setRole(e.target.value as ContactRole)}
              data-testid="select-contatto-role"
            >
              <option value="responsabile">Responsabile</option>
              <option value="distribuzione">Distribuzione</option>
              <option value="security">Security</option>
              <option value="segnalante">Segnalante</option>
            </select>
          </div>
        </div>
        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending}
            data-testid="btn-aggiungi-contatto"
          >
            {isPending ? 'Aggiunta…' : 'Aggiungi'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-aggiungi-contatto">
              {messaggioErrore()}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** Sezione "Contatti" del dettaglio cliente. */
export function ContactsSection({ customerId }: { customerId: number }) {
  const { can } = usePermissions();
  const { data: contatti, isLoading, isError, error } = useContacts(customerId);
  const removeContact = useRemoveContact(customerId);

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  return (
    <div data-testid="sezione-contatti">
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-contatti">
          Caricamento contatti…
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-contatti">
          {error instanceof Error ? error.message : 'Errore nel caricamento dei contatti'}
        </p>
      )}

      {!isLoading && !isError && contatti !== undefined && contatti.length === 0 && (
        <div className={styles.emptyState} data-testid="contatti-vuoti">
          Nessun contatto — le notifiche useranno il fallback di piattaforma.
        </div>
      )}

      {!isLoading && !isError && contatti !== undefined && contatti.length > 0 && (
        <table className={styles.table} data-testid="tabella-contatti">
          <thead>
            <tr>
              <th>Email</th>
              <th>Ruolo</th>
              <th>Aggiunto il</th>
              {puoGestire && <th>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {contatti.map((c) => (
              <tr key={c.id} data-testid={`contatto-row-${c.id}`}>
                <td>{c.email}</td>
                <td>
                  <span className={styles.badge}>{ETICHETTE_RUOLO[c.role]}</span>
                </td>
                <td>{c.createdAt}</td>
                {puoGestire && (
                  <td>
                    <button
                      className={`${styles.btnDanger} ${styles.btnSmall}`}
                      onClick={() => removeContact.mutate(c.id)}
                      disabled={removeContact.isPending}
                      data-testid={`btn-rimuovi-contatto-${c.id}`}
                    >
                      Rimuovi
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {removeContact.isError && (
        <span className={styles.inlineMutError} data-testid="errore-rimuovi-contatto">
          {removeContact.error instanceof Error
            ? removeContact.error.message
            : 'Errore durante la rimozione del contatto.'}
        </span>
      )}

      {/* Form aggiunta contatto (solo con PORTFOLIO_MANAGEMENT) */}
      {puoGestire && <NuovoContattoForm customerId={customerId} />}
    </div>
  );
}
